import React from 'react';
import { Box, Stack } from '@mui/material';

import { getBetDisplayLabel } from '../betLabels';

const hexToRgba = (hex, alpha) => {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }

  const value = Number.parseInt(hex.slice(1), 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const hexToTint = (hex, amount = 0.9) => {
  if (typeof hex !== 'string' || !/^#[0-9a-fA-F]{6}$/.test(hex)) {
    return null;
  }

  const value = Number.parseInt(hex.slice(1), 16);
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  const mix = (channel) => Math.round(channel + (255 - channel) * amount);
  return `rgb(${mix(red)}, ${mix(green)}, ${mix(blue)})`;
};

const formatRate = (rate) => {
  if (rate === null || rate === undefined || rate === '') {
    return '-';
  }

  const numericRate = Number(rate);
  return Number.isFinite(numericRate) ? numericRate.toFixed(2) : String(rate);
};

const RESPONSIVE_MODE = {
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
};

const popupCardSizeByResponsiveMode = {
  [RESPONSIVE_MODE.MOBILE]: {
    width: 'min(252px, calc(100vw - 24px))',
    maxWidth: '252px',
    padding: '9px',
  },
  [RESPONSIVE_MODE.DESKTOP]: {
    width: '340px',
    maxWidth: '340px',
    padding: '12px',
  },
};

const popupPositionByResponsiveMode = {
  [RESPONSIVE_MODE.MOBILE]: {
    left: '8px',
  },
  [RESPONSIVE_MODE.DESKTOP]: {
    left: '24px',
  },
};

const PopupCard = ({ label, item, responsiveMode = RESPONSIVE_MODE.DESKTOP }) => {
  const betColor = item.kind === 'bet' ? item.selection_color : null;
  const accentColor = betColor || (item.level === 'important' ? '#d32f2f' : '#2e7d32');
  const backgroundColor = betColor ? hexToTint(betColor, 0.9) || '#fff' : '#fff';
  const relationLabel = item.relation_label || '参加者';
  const cardSize = popupCardSizeByResponsiveMode[responsiveMode] ?? popupCardSizeByResponsiveMode[RESPONSIVE_MODE.DESKTOP];
  const isMobile = responsiveMode === RESPONSIVE_MODE.MOBILE;
  const selectionLabel = isMobile && item.kind === 'bet'
    ? getBetDisplayLabel(item.selection)
    : item.selection_label || '選択';

  return (
    <Box
      sx={{
        width: cardSize.width,
        maxWidth: cardSize.maxWidth,
        backgroundColor,
        color: '#263238',
        border: '1px solid #d9e2ec',
        borderLeft: `4px solid ${accentColor}`,
        borderRadius: '8px',
        boxShadow: betColor
          ? `0 10px 24px ${hexToRgba(betColor, 0.22) || 'rgba(0, 0, 0, 0.18)'}`
          : '0 10px 24px rgba(0, 0, 0, 0.18)',
        padding: cardSize.padding,
        pointerEvents: 'none',
        opacity: item.exiting ? 0 : 1,
        transform: item.exiting ? 'translateY(8px)' : 'translateY(0)',
        transition: 'opacity 0.4s ease, transform 0.4s ease',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Box sx={{ fontSize: '12px', fontWeight: 700, color: '#52616b' }}>
          {label}
        </Box>
        <Box
          sx={{
            px: 0.75,
            py: 0.25,
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 700,
            color: accentColor,
            backgroundColor: hexToRgba(accentColor, 0.12) || 'rgba(46, 125, 50, 0.12)',
            whiteSpace: 'nowrap',
          }}
        >
          {relationLabel}
        </Box>
      </Box>

      {item.kind === 'bet' ? (
        <Box>
          <Box sx={{ fontSize: '15px', fontWeight: 800, color: '#263238', mb: 0.75 }}>
            {item.name || 'Unknown'}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                borderRadius: '6px',
                color: betColor || '#263238',
                backgroundColor: hexToRgba(betColor, 0.16) || '#eef3f7',
                fontSize: '13px',
                fontWeight: 800,
                minWidth: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selectionLabel}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, fontSize: '13px', fontWeight: 700, whiteSpace: 'nowrap' }}>
              <span>{Number(item.point || 0).toLocaleString()}pt</span>
              <span>{formatRate(item.option_rate)}倍</span>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          {item.thumbnail ? (
            <Box
              component="img"
              src={item.thumbnail}
              alt=""
              sx={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                objectFit: 'cover',
                border: '1px solid #d9e2ec',
              }}
            />
          ) : null}
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ fontSize: '15px', fontWeight: 800, color: '#263238' }}>
              {item.name || 'Unknown'}
            </Box>
            <Box sx={{ fontSize: '12px', fontWeight: 700, color: '#607d8b' }}>
              ルームに参加しました
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const RoomPopups = ({ joinPopups = [], betPopups = [], responsiveMode = RESPONSIVE_MODE.DESKTOP }) => {
  const visibleItems = [
    ...joinPopups.slice(0, 3).map((item) => ({ ...item, popupLabel: '参加通知' })),
    ...betPopups.slice(0, 3).map((item) => ({ ...item, popupLabel: 'ベット通知' })),
  ].slice(0, 6);
  const popupPosition = popupPositionByResponsiveMode[responsiveMode] ?? popupPositionByResponsiveMode[RESPONSIVE_MODE.DESKTOP];

  if (!visibleItems.length) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        left: popupPosition.left,
        bottom: '88px',
        zIndex: 1301,
        pointerEvents: 'none',
      }}
    >
      <Stack spacing={1} sx={{ flexDirection: 'column-reverse' }}>
        {visibleItems.map((item) => (
          <PopupCard key={item.id} label={item.popupLabel} item={item} responsiveMode={responsiveMode} />
        ))}
      </Stack>
    </Box>
  );
};

export default RoomPopups;
